import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQItem[] = [
  {
    category: 'Procurement',
    question: 'How does company procurement work?',
    answer:
      'We use an enquiry-first process. After you enquire, we confirm your team size and requirements and provide tailored pricing. Once approved, we issue a tax invoice and send access details for your team.',
  },
  {
    category: 'Procurement',
    question: 'Can we get a purchase order or formal quote?',
    answer:
      'Yes. We can provide a formal written quote and accept purchase orders (including government and large organisations). Once the PO is confirmed, we issue a tax invoice and arrange access.',
  },
  {
    category: 'Team Access',
    question: 'How do we roll out access to our team?',
    answer:
      'After payment, we provide a unique enrolment link or access code for your organisation. Team members can self-enrol and complete the course at their own pace. We can also share a simple onboarding guide for internal rollout.',
  },
  {
    category: 'Team Access',
    question: 'Can we enrol team members in stages rather than all at once?',
    answer:
      'Yes. You can onboard staff progressively across the 12-month access period. Additional team members can be added later if needed.',
  },
  {
    category: 'Team Access',
    question: 'Is there a minimum or maximum number of participants?',
    answer:
      "There's no minimum. We support small teams through to larger rollouts, with pricing tailored to your organisation. For larger groups, we can discuss volume options.",
  },
  {
    category: 'CPD',
    question: 'Does this course count toward CPD?',
    answer:
      'Many participants record this as structured CPD depending on their professional body requirements. A certificate of completion is provided for CPD records. Please confirm CPD hour allocations with your professional body.',
  },
  {
    category: 'CPD',
    question: 'Which professional bodies recognise this for CPD?',
    answer:
      "CPD requirements vary by organisation and profession. Participants commonly log this type of training for built environment CPD (e.g., planning, architecture, landscape architecture, engineering), subject to each body's rules. We provide a completion certificate to support CPD claims.",
  },
  {
    category: 'Policy',
    question: 'Is this aligned with Better Placed and Connecting with Country policy?',
    answer:
      "Yes. The course is built around NSW's Better Placed policy and Connecting with Country principles — going beyond policy compliance to focus on genuine, meaningful implementation in real project delivery.",
  },
  {
    category: 'Next Steps',
    question: 'What happens after we enquire?',
    answer:
      "We'll respond within 1 business day to confirm your needs and share tailored pricing. If you proceed, we arrange payment, issue a tax invoice, and send access details. We confirm next steps by email.",
  },
];

const categoryIcons: Record<string, string> = {
  Procurement: 'ri-file-list-3-line',
  'Team Access': 'ri-team-line',
  CPD: 'ri-award-line',
  Policy: 'ri-government-line',
  'Next Steps': 'ri-arrow-right-circle-line',
};

const categories = Array.from(new Set(faqs.map((f) => f.category)));

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const { ref: listRef, isVisible: listVisible } = useScrollAnimation({ threshold: 0.05 });

  const allCategories = ['All', ...categories];
  const filtered = activeCategory === 'All' ? faqs : faqs.filter((f) => f.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 lg:py-32 bg-[#FDF8F3]">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 transition-all duration-700 ease-out ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-xl">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[#BA3D03] mb-4">
              FAQs
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E0503] leading-tight">
              Common Questions
            </h2>
            <p className="mt-4 text-[#5C3D2E] text-base leading-relaxed">
              Everything you need to know about procurement, team access, CPD, and getting started.
            </p>
          </div>

          {/* Category filter pills — horizontally scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
            {allCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setOpenIndex(null);
                }}
                className={`whitespace-nowrap flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer border ${
                  activeCategory === cat
                    ? 'bg-[#BA3D03] text-white border-[#BA3D03]'
                    : 'bg-white text-[#5C3D2E] border-[#E8C580] hover:border-[#BA3D03] hover:text-[#BA3D03]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Accordion list */}
        <div
          ref={listRef as React.RefObject<HTMLDivElement>}
          className="space-y-3"
        >
          {filtered.map((faq, index) => {
            const globalIndex = faqs.indexOf(faq);
            const isOpen = openIndex === globalIndex;
            return (
              <div
                key={globalIndex}
                className={`bg-white border rounded-xl overflow-hidden transition-all duration-500 ease-out ${
                  isOpen ? 'border-[#BA3D03] shadow-sm' : 'border-[#EDE0D4] hover:border-[#E8C580]'
                } ${listVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${index * 55}ms` }}
              >
                <button
                  onClick={() => toggleFAQ(globalIndex)}
                  className="w-full px-6 py-5 flex items-center gap-4 text-left cursor-pointer group"
                  aria-expanded={isOpen}
                >
                  {/* Category icon */}
                  <span
                    className={`w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg transition-colors duration-200 ${
                      isOpen ? 'bg-[#BA3D03] text-white' : 'bg-[#FDF8F3] text-[#BA3D03] group-hover:bg-[#FDEEE6]'
                    }`}
                  >
                    <i className={`${categoryIcons[faq.category] || 'ri-question-line'} text-base`}></i>
                  </span>

                  <div className="flex-1 min-w-0">
                    <span className="block text-[10px] font-semibold tracking-widest uppercase text-[#BA3D03]/70 mb-0.5">
                      {faq.category}
                    </span>
                    <span className="text-base font-semibold text-[#0E0503] leading-snug">
                      {faq.question}
                    </span>
                  </div>

                  <span
                    className={`w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${
                      isOpen ? 'bg-[#BA3D03] rotate-180' : 'bg-[#FDF8F3] rotate-0 group-hover:bg-[#FDEEE6]'
                    }`}
                  >
                    <i className={`ri-arrow-down-s-line text-lg ${isOpen ? 'text-white' : 'text-[#BA3D03]'}`}></i>
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-all ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                  style={{ transition: 'max-height 0.35s ease, opacity 0.25s ease' }}
                >
                  <div className="px-6 pb-6 pt-0 ml-1">
                    <div className="ml-0 sm:ml-[52px] border-l-2 border-[#E8C580] pl-4 sm:pl-5">
                      <p className="text-[#3D1203] text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-14 text-center transition-all duration-700 delay-300 ease-out ${
            listVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-[#5C3D2E] text-sm mb-4">Still have questions?</p>
          <a
            href="#contact"
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-[#BA3D03] text-white text-sm font-semibold rounded-lg hover:bg-[#9A3203] transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-mail-send-line"></i>
            Get in touch
          </a>
        </div>

      </div>
    </section>
  );
}
